/**
 * Drives the "confirm and pay" action in two explicit steps, rather than one
 * automatic sequence — so the caller can insert a price-change confirmation
 * between them (Pricing Engine E2E flows §4.3/§4.7, "Prevent stale pricing":
 * the price the frontend previewed and the price the backend actually froze
 * at Purchase creation can legitimately differ; the user must see and accept
 * the real one before payment proceeds).
 *
 * Step 1, `createPurchaseStep` — creates the Purchase, freezing the
 * backend-authoritative price. Never sends a frontend-calculated total —
 * `collectionCode`/`couponCode` are the only pricing-relevant inputs,
 * exactly matching `05_API_SPECIFICATION.md`'s Create Purchase contract (no
 * `amount` field exists on that request).
 *
 * Step 2, `proceedToCheckout` — creates the gateway payment attempt for the
 * already-frozen Purchase. Call this once the user has seen (and, if the
 * price changed, explicitly confirmed) the frozen amount.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cancelPurchase,
  createCheckout,
  createPurchase,
  retryCheckout,
  InvitationAlreadyPaidError,
  PurchaseRejectedError,
  PurchaseRateLimitedError,
} from './api';
import { openRazorpayCheckout, CheckoutUnavailableError, type RazorpayFailurePayload } from './razorpayCheckout';
import { relayCheckoutResult } from './relayCheckoutResult';
import { pollPurchaseUntilResolved, fetchPurchaseOutcomeOnce, PAYMENT_FAILED_MESSAGE, PAYMENT_UNAVAILABLE_MESSAGE } from './pollPurchaseOutcome';
import { SUCCESS_STATUSES, FAILURE_STATUSES } from './purchaseOutcome';
import type { CreateCheckoutResponse, Purchase } from './types';

// Fixed, reviewed copy only — never the raw backend/network error text
// (Pricing Engine architecture §12/§16: never expose internal backend
// errors or stack traces to the customer).
const PURCHASE_CREATION_FAILED_MESSAGE = 'We couldn’t start your purchase. Please try again.';
const CHECKOUT_CREATION_FAILED_MESSAGE =
  'We couldn’t start the payment. Your order is saved — please try again.';
const NO_PURCHASE_TO_CHECKOUT_MESSAGE =
  'We couldn’t find your order. Please start again.';
const NO_CHECKOUT_TO_PAY_MESSAGE = 'We couldn’t start payment. Please try again.';
const PURCHASE_NO_LONGER_ACTIVE_MESSAGE = 'This order is no longer active. Please start again.';

function mintKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}



export type CreatePurchasePhase =
  | 'idle'
  | 'creating-purchase'
  | 'awaiting-confirmation'
  | 'creating-checkout'
  | 'ready-for-payment'
  | 'awaiting-gateway'
  | 'confirming'
  | 'confirmation-pending'
  | 'paid'
  | 'payment-failed'
  | 'cancelling'
  | 'error';

export interface UseCreatePurchaseResult {
  phase: CreatePurchasePhase;
  purchase: Purchase | null;
  checkout: CreateCheckoutResponse | null;
  error: string | null;
  /**
   * `idempotencyKey` is mandatory (not optional) on purpose — see
   * `purchase/api.ts`'s `createPurchase` doc comment. Every caller must mint
   * a fresh UUID per logical attempt (e.g. `useRef(crypto.randomUUID())` at
   * the point the Buy Now control is rendered) and pass it through here.
   */
  createPurchaseStep: (input: {
    collectionCode?: string;
    packCode?: string;
    couponCode?: string;
    invitationId?: string;
    idempotencyKey: string;
  }) => Promise<Purchase | null>;
  /**
   * Accepts an optional explicit Purchase to proceed with, defaulting to
   * this hook's own `purchase` state. The explicit form exists so a caller
   * can chain straight from `createPurchaseStep`'s resolved value in the
   * same async function, without a stale closure over `purchase` state that
   * has not yet re-rendered — calling `proceedToCheckout()` with no
   * argument immediately after `await createPurchaseStep(...)` in one
   * function body would otherwise silently no-op (the `purchase` this
   * callback closed over is whatever it was at the last render, not the
   * one `setPurchase` just set).
   */
  proceedToCheckout: (explicitPurchase?: Purchase) => Promise<CreateCheckoutResponse | null>;
  /**
   * Explicit cancellation (API spec, Cancel Purchase) — used when the user
   * backs out after seeing a price change rather than proceeding to
   * payment. Best-effort: if the cancel call itself fails, state is still
   * reset locally (the backend's own 30-minute expiry sweep,
   * `01_PURCHASE_DOMAIN.md §4`, is the safety net either way — a failed
   * cancel request must never trap the user on a dead-end screen).
   */
  cancel: (reason?: string) => Promise<void>;
  reset: () => void;
  /**
   * Opens the Razorpay Checkout.js widget for the current `checkout`/`purchase`.
   * A Razorpay success callback is never trusted directly — see
   * `razorpayCheckout.ts`'s module doc — this polls `getPurchase` afterward
   * for the backend-verified outcome, up to `MAX_POLL_ATTEMPTS`, before
   * giving up (phase `confirmation-pending`, not an error — the payment may
   * still be genuinely processing).
   */
  /**
   * Opens the gateway widget. Pass BOTH the checkout and the purchase explicitly
   * when chaining create → checkout → pay inside one event handler: the callback
   * closes over hook state from the render it was made in, which on a first
   * purchase is still null for both.
   */
  openPayment: (
    explicitCheckout?: CreateCheckoutResponse,
    explicitPurchase?: Purchase,
  ) => Promise<void>;
  /** One manual re-check from `confirmation-pending` — e.g. a "Check status" button, or on page focus. */
  refreshStatus: () => Promise<void>;
}

export function useCreatePurchase(): UseCreatePurchaseResult {
  const [phase, setPhase] = useState<CreatePurchasePhase>('idle');
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [checkout, setCheckout] = useState<CreateCheckoutResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Bumped at the start of every `createPurchaseStep` call. Lets a
  // still-in-flight `cancel()` (see below) recognize that a *newer* attempt
  // has since started and skip its own final `reset()` instead of clobbering
  // that newer attempt's state — the exact race caught in code review (a
  // second Buy Now click during the unavoidable async window of a cancel
  // wiping out the second attempt's freshly-created Purchase).
  const attemptIdRef = useRef(0);
  const retryKeyRef = useRef<{ purchaseId: string; key: string } | null>(null);

  const createPurchaseStep = useCallback(
    async ({
      collectionCode,
      packCode,
      couponCode,
      invitationId,
      idempotencyKey,
    }: {
      collectionCode?: string;
      packCode?: string;
      couponCode?: string;
      invitationId?: string;
      idempotencyKey: string;
    }): Promise<Purchase | null> => {
      const attemptId = ++attemptIdRef.current;
      setError(null);
      setPhase('creating-purchase');
      try {
        const created = await createPurchase(
          { collectionCode, packCode, couponCode, invitationId },
          idempotencyKey,
        );
        // Superseded (a newer attempt has since started) — return null, same
        // as the error path below, never the created Purchase itself: the
        // caller (`CheckoutPage.handleConfirmAndPay`) chains straight onto a
        // truthy return value with `proceedToCheckout(created)`, and this
        // Purchase was never actually stored in this hook's state, so
        // treating it as usable would let the caller act on a Purchase this
        // hook itself has already abandoned (caught in code review).
        if (attemptIdRef.current !== attemptId) return null;
        setPurchase(created);
        setPhase('awaiting-confirmation');
        return created;
      } catch (err) {
        if (attemptIdRef.current !== attemptId) return null;
        setPhase('error');
        if (err instanceof PurchaseRateLimitedError) {
          setError(err.message);
        } else if (err instanceof PurchaseRejectedError) {
          setError(err.message);
        } else {
          setError(PURCHASE_CREATION_FAILED_MESSAGE);
        }
        return null;
      }
    },
    [],
  );

  const proceedToCheckout = useCallback(
    async (explicitPurchase?: Purchase): Promise<CreateCheckoutResponse | null> => {
      const target = explicitPurchase ?? purchase;
      if (!target) {
        // No Purchase to check out with — surface this as a real error
        // rather than a silent no-op (a silent no-op here was exactly the
        // stale-closure bug caught in code review; guarding it explicitly
        // makes any future misuse visible instead of invisible again).
        setPhase('error');
        setError(NO_PURCHASE_TO_CHECKOUT_MESSAGE);
        return null;
      }
      // Route on the Purchase's own state, not on how we got here: per-
      // invitation dedup (Create Purchase with invitationId) hands back an
      // EXISTING open Purchase, which may already have a gateway order or
      // even be paid. POST /checkout only accepts CHECKOUT_CREATED.
      if (SUCCESS_STATUSES.has(target.status)) {
        // Already paid — never start a second charge for the same intent.
        setPurchase(target);
        setError(null);
        setPhase('paid');
        return null;
      }
      if (FAILURE_STATUSES.has(target.status)) {
        // Terminal: drop it so the next Confirm & Pay creates a new Purchase
        // (the backend never dedups onto a terminal one).
        setPurchase(null);
        setPhase('error');
        setError(PURCHASE_NO_LONGER_ACTIVE_MESSAGE);
        return null;
      }
      setError(null);
      setPhase('creating-checkout');
      try {
        let checkoutResult: CreateCheckoutResponse;
        if (target.status === 'PAYMENT_PENDING') {
          // One key per Purchase until a retry succeeds, so a user retrying
          // after a 502 collapses onto the same gateway order server-side.
          if (retryKeyRef.current?.purchaseId !== target.id) {
            retryKeyRef.current = { purchaseId: target.id, key: mintKey() };
          }
          checkoutResult = await retryCheckout(target.id, retryKeyRef.current.key);
          retryKeyRef.current = null;
        } else {
          checkoutResult = await createCheckout(target.id);
        }
        setPurchase(target);
        setCheckout(checkoutResult);
        setPhase('ready-for-payment');
        // Return the fresh checkout so callers can chain openPayment(checkout)
        // without stale-closure — openPayment() reading hook state gets
        // whatever was set at last render, not the value just set above
        // (Bug 3b: wrong amount shown in Razorpay when switching packs).
        return checkoutResult;
      } catch (err) {
        setPhase('error');
        if (err instanceof PurchaseRateLimitedError) {
          setError(err.message);
        } else if (err instanceof InvitationAlreadyPaidError) {
          setError(err.message);
        } else {
          setError(CHECKOUT_CREATION_FAILED_MESSAGE);
        }
        return null;
      }
    },
    [purchase],
  );

  const reset = useCallback(() => {
    setPhase('idle');
    setPurchase(null);
    setCheckout(null);
    setError(null);
  }, []);

  const cancel = useCallback(
    async (reason?: string) => {
      // Snapshot before the first await — if a *newer* createPurchaseStep
      // call starts while this cancel's network request is still in flight,
      // attemptIdRef will have moved on by the time we get back, and the
      // final `reset()` below must not run (it would wipe the newer
      // attempt's Purchase/checkout state instead of the one being
      // cancelled). Caught in code review — see attemptIdRef's comment.
      const attemptId = attemptIdRef.current;
      const target = purchase;
      // Set synchronously, before any await, so the very next render
      // disables "Confirm & Pay" (via `isBusy` in CheckoutPage) for the
      // entire cancel window — this is what actually closes the race: a
      // second click cannot fire `createPurchaseStep` while this is
      // in-flight, so `attemptIdRef` in practice never even needs to move for
      // this specific path. The ref check above remains as defense in depth
      // for any other caller of `cancel`/`createPurchaseStep` this hook
      // gains later.
      setPhase('cancelling');
      if (target) {
        await cancelPurchase(target.id, reason).catch(() => undefined);
      }
      if (attemptIdRef.current !== attemptId) return;
      reset();
    },
    [purchase, reset],
  );

  // Guards every state-setting call in the poll loop and in openPayment's
  // async gateway-load step — without this, navigating away from the page
  // mid-poll (or while Checkout.js is still loading) leaves ~60s of
  // `setState` calls firing on an unmounted component. `attemptIdRef` alone
  // does NOT cover this: it only detects a *newer attempt superseding an
  // older one*, not "no component is listening anymore." Caught in code
  // review.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const pollUntilResolved = useCallback(
    async (purchaseId: string, attemptId: number) => {
      const result = await pollPurchaseUntilResolved(
        purchaseId,
        () => mountedRef.current && attemptIdRef.current === attemptId,
      );
      if (!result) return; // unmounted, or superseded by a newer attempt
      if (result.kind === 'confirmation-pending') {
        // Gave up, but this is NOT a failure — the payment may still be
        // genuinely processing (webhook delay, frontend disconnect). Never
        // claim success OR failure here; `refreshStatus` lets the user (or
        // the page, on refocus) check again without reopening the widget.
        setPhase('confirmation-pending');
        return;
      }
      setPurchase(result.purchase);
      setPhase(result.kind);
      if (result.kind === 'payment-failed') {
        setError(
          result.purchase.status === 'CANCELLED'
            ? 'This order was cancelled. Any payment made has been automatically refunded.'
            : PAYMENT_FAILED_MESSAGE,
        );
      }
    },
    [],
  );

  const openPayment = useCallback(
    async (explicitCheckout?: CreateCheckoutResponse, explicitPurchase?: Purchase) => {
    // BOTH the checkout and the purchase must be passable explicitly, because this
    // callback closes over `checkout`/`purchase` from the render it was created in.
    // A caller that chains create → checkout → pay inside ONE event handler (which
    // is the whole credit-pack flow) still holds the click-time closure, where both
    // are null on a first purchase — so reading hook state made the guard below fire
    // and Razorpay never opened, leaving an orphan CHECKOUT_CREATED Purchase and a
    // live gateway order behind. On a second click it was worse than a no-op: the
    // widget opened with the fresh order but `purchaseId` came from the PREVIOUS
    // purchase, so verification ran against the wrong one.
    //
    // Bug 3b threaded `checkout` for the price and stopped there; `purchase` has the
    // identical lifetime problem and needs the identical treatment.
    const activeCheckout = explicitCheckout ?? checkout;
    const activePurchase = explicitPurchase ?? purchase;
    if (!activeCheckout || !activePurchase) {
      setPhase('error');
      setError(NO_CHECKOUT_TO_PAY_MESSAGE);
      return;
    }
    const attemptId = ++attemptIdRef.current;
    const purchaseId = activePurchase.id;
    setError(null);
    setPhase('awaiting-gateway');

    try {
      await openRazorpayCheckout({
        keyId: activeCheckout.checkout.keyId,
        gatewayOrderId: activeCheckout.checkout.gatewayOrderId,
        amountPaise: activeCheckout.checkout.amountPaise,
        currency: activeCheckout.checkout.currency,
        // Enables the saved-card tab. Null-safe: openRazorpayCheckout omits the
        // option entirely when this is null, so a buyer whose customer could not
        // be created still pays normally, just without saved cards.
        customerId: activeCheckout.checkout.customerId,
        onSuccess: (response) => {
          if (!mountedRef.current || attemptIdRef.current !== attemptId) return;
          setPhase('confirming');
          // Advisory relay of the gateway's triple for server-side HMAC
          // verification + attempt recording. Fire-and-forget: its result
          // never drives the phase — only the poll below does.
          void relayCheckoutResult(purchaseId, response);
          void pollUntilResolved(purchaseId, attemptId);
        },
        onDismiss: () => {
          if (!mountedRef.current || attemptIdRef.current !== attemptId) return;
          // Purchase is still PAYMENT_PENDING on the backend, and this
          // Purchase's gateway order was never consumed — the SAME `checkout`
          // can be reopened without a new createCheckout call.
          setPhase('ready-for-payment');
        },
        onFailure: (_failure: RazorpayFailurePayload) => {
          if (!mountedRef.current || attemptIdRef.current !== attemptId) return;
          setPhase('payment-failed');
          setError(PAYMENT_FAILED_MESSAGE);
        },
      });
    } catch (err) {
      // Checkout.js itself failed to load (ad-blocker, offline, CSP) — this
      // is NOT the widget's onFailure (that only fires once the widget is
      // actually open); without this catch, the promise rejection was
      // unhandled and the UI was stuck on "Opening secure payment…" forever.
      // Caught in code review.
      if (!mountedRef.current || attemptIdRef.current !== attemptId) return;
      setPhase('payment-failed');
      setError(err instanceof CheckoutUnavailableError ? PAYMENT_UNAVAILABLE_MESSAGE : PAYMENT_FAILED_MESSAGE);
    }
    },
    [checkout, purchase, pollUntilResolved],
  );

  const refreshStatus = useCallback(async () => {
    if (!purchase) return;
    const attemptId = attemptIdRef.current;
    try {
      const result = await fetchPurchaseOutcomeOnce(purchase.id);
      if (!mountedRef.current || attemptIdRef.current !== attemptId) return;
      if (result) {
        setPurchase(result.purchase);
        setPhase(result.kind);
        if (result.kind === 'payment-failed') setError(PAYMENT_FAILED_MESSAGE);
      }
    } catch {
      // Best-effort — stay on confirmation-pending, the user can try again.
    }
  }, [purchase]);

  return {
    phase,
    purchase,
    checkout,
    error,
    createPurchaseStep,
    proceedToCheckout,
    cancel,
    reset,
    openPayment,
    refreshStatus,
  };
}
